import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useOrpc } from '@/_shared/hooks/useOrpc';
import type { ContextHubItem } from '@/_shared/lib/context-hub';

interface SkillMenuProps {
  /** Text typed after the leading "/" — used for substring filtering. */
  query: string;
  /** Skill IDs already loaded into the chat — excluded from the menu. */
  alreadyLoadedIds: Set<string>;
  onSelect: (skill: { id: string; title: string }) => void;
  onClose: () => void;
}

/**
 * Autocomplete menu shown when the user types "/" in the chat composer.
 * Lists promoted skills (`adherence=skill`, `skillEnabled=true`) for the
 * workspace that are not already loaded into the current session.
 *
 * Fetches via `orpc.contextHub.list` — results come from the QueryClient
 * cache (shared with Chat.tsx) so no additional network request is made.
 */
export function SkillMenu({ query, alreadyLoadedIds, onSelect, onClose }: SkillMenuProps): React.ReactElement | null {
  const orpc = useOrpc();
  const [activeIndex, setActiveIndex] = useState(0);

  const { data, isPending } = useQuery(orpc.contextHub.list.queryOptions({ input: {} }));

  const allSkills: ContextHubItem[] = (data?.items ?? []).filter(
    (item: ContextHubItem) =>
      item.kind === 'note' && item.adherence === 'skill' && item.skillEnabled && !alreadyLoadedIds.has(item.id),
  );

  const filteredSkills = query
    ? allSkills.filter((s) => s.title.toLowerCase().includes(query.toLowerCase()))
    : allSkills;

  // Reset active index when filtered list identity changes (stable key, not just length)
  const filteredSkillIds = filteredSkills.map((s) => s.id).join(',');
  useEffect(() => {
    setActiveIndex(0);
  }, [filteredSkillIds]);

  const listRef = useRef<HTMLUListElement>(null);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, filteredSkills.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Enter' && filteredSkills.length > 0) {
        e.preventDefault();
        const skill = filteredSkills[activeIndex];
        if (skill) onSelect({ id: skill.id, title: skill.title });
        return;
      }
    },
    [activeIndex, filteredSkills, onSelect, onClose],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    const ul = listRef.current;
    if (!ul) return;
    const item = ul.children[activeIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  if (isPending) {
    return (
      <div
        role="listbox"
        aria-label="Promoted skills"
        className="absolute bottom-full left-0 z-50 mb-1 w-72 overflow-hidden rounded-xl border border-border/60 bg-card shadow-[0_8px_32px_-8px_rgba(0,0,0,0.16)]"
        data-testid="skill-menu"
      >
        <div className="px-3 py-3 text-center text-xs text-muted-foreground">Loading skills…</div>
      </div>
    );
  }

  if (filteredSkills.length === 0) {
    return (
      <div
        role="listbox"
        aria-label="Promoted skills"
        className="absolute bottom-full left-0 z-50 mb-1 w-72 overflow-hidden rounded-xl border border-border/60 bg-card shadow-[0_8px_32px_-8px_rgba(0,0,0,0.16)]"
        data-testid="skill-menu"
      >
        <div className="px-3 py-3 text-center text-xs text-muted-foreground">No matching skills</div>
      </div>
    );
  }

  return (
    <div
      id="skill-menu-listbox"
      role="listbox"
      aria-label="Promoted skills"
      aria-activedescendant={
        filteredSkills[activeIndex]?.id ? `skill-option-${filteredSkills[activeIndex].id}` : undefined
      }
      className="absolute bottom-full left-0 z-50 mb-1 w-72 overflow-hidden rounded-xl border border-border/60 bg-card shadow-[0_8px_32px_-8px_rgba(0,0,0,0.16)]"
      data-testid="skill-menu"
    >
      <ul ref={listRef} className="max-h-64 overflow-y-auto py-1">
        {filteredSkills.map((skill, idx) => (
          <li key={skill.id} id={`skill-option-${skill.id}`} role="option" aria-selected={idx === activeIndex}>
            <button
              type="button"
              onClick={() => onSelect({ id: skill.id, title: skill.title })}
              className={`w-full px-3 py-2 text-left text-sm transition-colors ${
                idx === activeIndex
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
              }`}
              data-testid={`skill-option-${skill.id}`}
            >
              <span className="font-medium">{skill.title}</span>
              {skill.summary && <span className="ml-2 text-xs text-muted-foreground/70">{skill.summary}</span>}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
